/**
 * TanStack Query keys для task-module (FE-S12.1-11).
 *
 * Иерархия:
 *   ['tasks', { projectId?, filter, groupBy }] — list query
 *   ['tasks', taskId]                          — detail
 *   ['tasks', taskId, 'comments']              — comments list
 *   ['tasks', taskId, 'audit']                 — audit timeline
 *   ['tasks', taskId, 'worklog']               — worklog entries
 *   ['tasks', taskId, 'dependencies']          — deps list
 *   ['projects', ...]                          — project hierarchy
 *   ['saved-views', ...]
 *   ['blueprints', ...]
 *   ['notifications', ...]
 *
 * Все mutation hooks (FE-S12.1-12) делают `invalidate({ queryKey: taskKeys.detail(id) })`
 * + smart invalidation list queries после оптимистичного апдейта.
 */

export type TaskFilter = {
  assignee_user_ids?: readonly string[];
  project_ids?: readonly string[];
  state_groups?: readonly string[];
  due_buckets?: readonly string[];
  priorities?: readonly string[];
  labels?: readonly string[];
  subject_types?: readonly string[];
};

export type GroupBy =
  | 'project'
  | 'assignee'
  | 'state_group'
  | 'priority'
  | 'due_bucket'
  | 'label'
  | null;

export const taskKeys = {
  all: ['tasks'] as const,
  byFilter: (filter: TaskFilter, groupBy?: GroupBy) =>
    ['tasks', 'list', { filter, groupBy: groupBy ?? null }] as const,
  byProject: (projectId: string, filter?: TaskFilter, groupBy?: GroupBy) =>
    ['tasks', 'list', { projectId, filter: filter ?? {}, groupBy: groupBy ?? null }] as const,
  detail: (taskId: string) => ['tasks', taskId] as const,
  comments: (taskId: string) => ['tasks', taskId, 'comments'] as const,
  audit: (taskId: string) => ['tasks', taskId, 'audit'] as const,
  worklog: (taskId: string) => ['tasks', taskId, 'worklog'] as const,
  dependencies: (taskId: string) => ['tasks', taskId, 'dependencies'] as const,
};

export const projectKeys = {
  all: ['projects'] as const,
  list: ['projects', 'list'] as const,
  detail: (projectId: string) => ['projects', projectId] as const,
  states: (projectId: string) => ['projects', projectId, 'states'] as const,
  /** Кросс-проектный справочник статусов одним запросом (BE-19). Ключ НЕ вложен в
   *  `['projects', pid]`, поэтому per-project invalidate его не накрывает —
   *  сбрасывать только через `invalidateStates()`. */
  statesBatch: (projectIds: readonly string[]) =>
    ['projects', 'states-batch', [...projectIds].sort().join(',')] as const,
  statesBatchAll: ['projects', 'states-batch'] as const,
  fieldDefs: (projectId: string) => ['projects', projectId, 'field-definitions'] as const,
  members: (projectId: string) => ['projects', projectId, 'members'] as const,
};

/**
 * Сброс справочника статусов ОБОИХ видов: per-project и кросс-проектного батча.
 *
 * Единственная точка намеренно: у батча свой ключ, и `invalidateQueries(states(pid))`
 * его не заматчит. Разложи это по шести местам вызова — и первый же забытый
 * вернёт баг «статус переименовали, а на общем дашборде старое имя до staleTime».
 */
export function invalidateStates(
  qc: { invalidateQueries: (f: { queryKey: readonly unknown[] }) => unknown },
  projectId: string,
): void {
  qc.invalidateQueries({ queryKey: projectKeys.states(projectId) });
  qc.invalidateQueries({ queryKey: projectKeys.statesBatchAll });
}

export const savedViewKeys = {
  all: ['saved-views'] as const,
  list: (scope?: 'user' | 'workspace') => ['saved-views', 'list', scope ?? 'all'] as const,
  detail: (id: string) => ['saved-views', id] as const,
};

export const blueprintKeys = {
  all: ['blueprints'] as const,
  list: ['blueprints', 'list'] as const,
  detail: (bpId: string) => ['blueprints', bpId] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  aggregate: ['notifications', 'aggregate'] as const,
  settings: ['notifications', 'settings'] as const,
};
